import { useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useRouterState } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { alerts, notifications, photos } from '@cwos/api-client';
import { AppShell, initials } from '@cwos/ui-admin';
import type { AttentionItem, FindResult, PanelNotification } from '@cwos/ui-admin';
import { useAuth } from '../../lib/auth.js';
import { NAV_MAP, useNav } from '../navigation.js';
import { filterNavGroups, mayReadAlerts, usePermissions } from '../permissions.js';
import { clearRememberedOrg, writeRememberedOrg } from '../rememberedOrg.js';
import { AskPhibian } from '../intelligence/global/index.js';
import { alertToAttentionItem, deriveAttentionCount, sortAttentionAlerts } from './attention.js';
import { buildAccountItems } from './accountMenu.js';
import { pageTitle } from './documentTitle.js';
import { HubCards } from './HubCards.js';
import { useGlobalSearch } from './useGlobalSearch.js';
import { toPanelNotification } from '../notifications/notificationsLogic.js';

const PANEL_LIMIT = 8;

function displayName(me: { name?: string; email?: string } | null): string {
  if (!me) return '—';
  const name = me.name?.trim();
  if (name) return name;
  return me.email?.trim() ?? '—';
}

/**
 * Root layout (doc 04 §1). Wraps every authenticated route in the AppShell and
 * renders the matched route through `<Outlet/>`. Owns the header's live data:
 * the attention badge/panel (from the alerts domain, 60s poll) and the global
 * search results (tenant-scoped, permission-filtered).
 */
export function ShellLayout() {
  const { me, logout, switchOrg } = useAuth();
  const navigate = useNav();
  const {
    permissions,
    has,
    isLoading: permissionsLoading,
    isError: permissionsError,
  } = usePermissions();
  /**
   * `GET /api/alerts` takes `alerts:read` AND `directory:read`, and this read
   * was gated on neither — polling a two-key route every 60 seconds, on every
   * screen, for a reader who may hold no part of it. An `analyst` holds no part
   * of it by design (1757290000000), so the shell 403'd once a minute for the
   * whole session and the panel called it "Nothing needs your attention".
   *
   * Refused is claimed only from a RESOLVED set. `usePermissions` answers a
   * failed read with an empty one for default-deny reasons, which is right for
   * not firing the request and wrong for telling somebody they lack access:
   * while the set is unknown the panel says loading, not withheld.
   */
  const canReadAlerts = mayReadAlerts(has);
  const alertsRefused = !permissionsLoading && !permissionsError && !canReadAlerts;
  const nav = filterNavGroups(NAV_MAP, permissions);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const [findQuery, setFindQuery] = useState('');

  const attention = useQuery({
    queryKey: ['alerts', 'attention-count'],
    queryFn: () => alerts.list({ limit: 100 }),
    refetchInterval: 60_000,
    enabled: canReadAlerts,
  });

  const attentionCount = attention.data ? deriveAttentionCount(attention.data.total) : 0;

  /**
   * THE READER'S UNREAD NOTIFICATIONS, on the bell and IN its panel. Every new
   * Needs Attention item notifies the people who act on it, so the bell
   * carries both numbers (coral = items open, sky = notifications not yet
   * read) and the panel lists the newest unread ones. One read answers both:
   * `unread` is the server's uncapped count, `notifications` the page. Under
   * the `['notifications']` key the Notification Centre invalidates on read.
   */
  const qc = useQueryClient();
  const canReadNotifications = has('notification:read');
  const unread = useQuery({
    queryKey: ['notifications', 'panel'],
    queryFn: () => notifications.list({ unreadOnly: true, limit: PANEL_LIMIT }),
    refetchInterval: 60_000,
    enabled: canReadNotifications,
  });
  const notificationItems = useMemo<PanelNotification[]>(
    () => (unread.data?.notifications ?? []).map((n) => toPanelNotification(n)),
    [unread.data],
  );
  const refreshNotifications = () => void qc.invalidateQueries({ queryKey: ['notifications'] });
  const markNotificationRead = useMutation({
    mutationFn: (id: string) => notifications.markRead(id),
    onSuccess: refreshNotifications,
  });
  const markAllNotificationsRead = useMutation({
    mutationFn: () => notifications.markAllRead(),
    onSuccess: refreshNotifications,
  });

  const attentionItems = useMemo<AttentionItem[]>(() => {
    if (!attention.data) return [];
    return sortAttentionAlerts(attention.data.alerts)
      .slice(0, PANEL_LIMIT)
      .map((a) => alertToAttentionItem(a));
  }, [attention.data]);

  const search = useGlobalSearch(findQuery);

  /**
   * What a route change does for someone who is not looking at the screen.
   *
   * A single-page app replaces the contents of `<main>` without any of the
   * signals a real page load gives: the title does not change, and focus stays
   * on whatever nav item was clicked — so a screen reader says nothing, and the
   * next Tab press continues from the rail rather than from the new screen.
   *
   * Setting the title announces the destination; moving focus to `<main>` (which
   * carries `tabIndex={-1}` for exactly this) puts the reading cursor at the top
   * of the new content. The first render is deliberately title-only: focus
   * belongs wherever the browser put it on load, not stolen on arrival.
   */
  const landed = useRef(false);
  useEffect(() => {
    document.title = pageTitle(pathname);
    if (!landed.current) {
      landed.current = true;
      return;
    }
    document.getElementById('main')?.focus();
  }, [pathname]);

  const name = displayName(me);
  const roleLabel = me && me.roles.length > 0 ? me.roles.join(' · ') : undefined;
  const isDemo = me?.orgSlug === 'cwos-demo';

  // Persist the last-known org as a cosmetic hint for the pre-auth sign-in
  // screen (see features/rememberedOrg.ts). Authorization is unaffected — the
  // org is still resolved server-side via /auth/me and switched via /auth/switch-org.
  useEffect(() => {
    if (me?.orgName) {
      writeRememberedOrg({ name: me.orgName, slug: me.orgSlug ?? null });
    }
  }, [me?.orgName, me?.orgSlug]);

  const memberships = me?.memberships ?? [];
  const otherOrgs = memberships
    .filter((m) => m.orgId !== me?.orgId)
    .map((m) => ({ orgId: m.orgId, orgName: m.orgName }));

  const accountItems = buildAccountItems(permissions, {
    onMyProfile: () => navigate('/me/profile'),
    onViewAsWorker: () => navigate('/preview/worker'),
    onSettings: () => navigate('/settings'),
    onNotifications: () => navigate('/notifications'),
    onNotificationPreferences: () => navigate('/settings/notification-preferences'),
    onIntegrations: () => navigate('/settings/records'),
    onHelp: () => navigate('/help'),
    onSignOut: () => {
      clearRememberedOrg();
      void logout();
    },
  });

  const handleFindSelect = (r: FindResult) => {
    setFindQuery('');
    if (r.href) navigate(r.href);
  };

  const handleAttentionSelect = (item: AttentionItem) => {
    if (item.href) navigate(item.href);
  };

  return (
    <>
      <AppShell
        nav={nav}
        // The material environment of the work surface. The whole
        // Intelligence family — Ask Phibian, Insights, Recommendations,
        // Agents, Library — is ONE Water environment: navigating between its
        // rooms never leaves the water, and the attribute is computed from
        // the path synchronously so route changes inside the hub cannot
        // flash back to canvas. Every other hub is the light operational
        // canvas, whatever intelligence accents float on it.
        environment={
          pathname === '/intelligence' ||
          pathname.startsWith('/intelligence/') ||
          pathname === '/insights' ||
          pathname.startsWith('/insights/') ||
          pathname === '/agents' ||
          pathname.startsWith('/agents/') ||
          pathname === '/library' ||
          pathname.startsWith('/library/')
            ? 'water'
            : 'canvas'
        }
        // An empty rail is a sentence, and until `/auth/me` answers it is the
        // wrong one. `filterNavGroups` over an empty permission set returns `[]`,
        // which is exactly what a genuinely permissionless account returns — so
        // every administrator's first sight of the product was a chrome with no
        // navigation in it. Same defect the state sweep fixed on Settings; this
        // is the surface it is read on first.
        navLoading={permissionsLoading}
        user={{
          name,
          initials: initials(name),
          // Through the ONE canonical contract, not a hand-built URL — a second
          // place that knows how a photo URL is shaped is a second place for it
          // to drift (docs/PROFILE-PHOTO-CONTRACT.md).
          photoSrc: photos.personPhotoSrc({
            userId: me?.userId,
            photoUpdatedAt: me?.photoUpdatedAt,
          }),
          email: me?.email ?? null,
          roleLabel,
          orgName: me?.orgName ?? null,
          isDemo,
        }}
        attentionCount={attentionCount}
        currentPath={pathname}
        onNavigate={navigate}
        brandLabel="phibian"
        accountItems={accountItems}
        otherOrgs={otherOrgs}
        onSwitchOrg={(orgId) => void switchOrg(orgId)}
        findQuery={findQuery}
        onFindQueryChange={setFindQuery}
        findResults={search.results}
        findLoading={search.loading}
        findError={search.error}
        findPartial={search.partial}
        findUnresolved={search.unresolved}
        findNothingSearchable={search.nothingSearchable}
        onFindSelect={handleFindSelect}
        attentionItems={attentionItems}
        attentionLoading={attention.isLoading || permissionsLoading}
        attentionError={attention.isError}
        attentionRefused={alertsRefused}
        onAttentionSelect={handleAttentionSelect}
        onAttentionOpenAll={() => navigate('/attention')}
        notificationsUnread={unread.data?.unread}
        onOpenNotifications={canReadNotifications ? () => navigate('/notifications') : undefined}
        notificationItems={notificationItems}
        // Opening one marks it read and follows the same route the Centre
        // would; one with no Admin destination is still marked read.
        onNotificationSelect={(item) => {
          markNotificationRead.mutate(item.id);
          if (item.href) navigate(item.href);
        }}
        onMarkAllNotificationsRead={() => markAllNotificationsRead.mutate()}
      >
        {/* The in-hub navigation cards (Phase A ruling): rendered by the
            shell rather than by each hub screen, so a hub page cannot forget
            its own card row and the row cannot drift from hubs.ts. */}
        <HubCards path={pathname} />
        <Outlet />
      </AppShell>
      {/*
       * The global assistant, mounted OUTSIDE the shell's grid rather than
       * beside `<Outlet/>`: it is fixed-position chrome that belongs to the
       * app, not to whichever screen happens to be rendered, and putting it
       * inside `<main>` would make it a child of the region the route change
       * effect above moves focus into.
       *
       * It renders nothing at all for a caller without `intelligence:use`, so
       * the shell needs no gate of its own here — and must not grow one: two
       * places deciding the same thing is how they come to disagree.
       */}
      <AskPhibian />
    </>
  );
}
